// Contracts for «Red Envelopes» (hongbao, 抢红包): shared rounds per table, socket events, public state.
//
// Tables. A stake (`room` = 18 / 88 / 888) has one or more tables of 8 seats. Table 1 always exists; when every table
// of a stake is taken (8 seated or the round already runs), the next stake opens another one (numbers without «4»:
// `HB_TABLE_NOS`, at most `HB_MAX_TABLES` per stake), a spare empty table closes. The player does not choose the table:
// the server seats him at the open table of the stake with the most players and a free seat.
// Table id: `hongbaoTableId(room, table)` = "88" for table 1 (as before tables existed), "88-2" for table 2 …;
// it is `GameRound.room` of the table's rounds and the socket room `game:hongbao:<tableId>`.
// Older clients / servers: every new field below is optional; absent `table` = table 1.
import { z } from 'zod';
import { zIdempotencyKey } from '../common';
import type { ApiError } from '../errors';

/** Stakes (lucky numbers, no «4»). */
export const HB_ROOMS = [18, 88, 888] as const;
export type HongbaoRoom = (typeof HB_ROOMS)[number];
export const zHongbaoRoom = z.union([z.literal(18), z.literal(88), z.literal(888)]);
/** Table numbers of a stake, in the order they open (no «4»). */
export const HB_TABLE_NOS = [1, 2, 3, 5, 6, 7, 8, 9] as const;
/** At most this many tables per stake; beyond — `ROOM_FULL`. */
export const HB_MAX_TABLES = HB_TABLE_NOS.length;

/** "88" (table 1) / "88-2": `GameRound.room`, socket room suffix */
export function hongbaoTableId(room: HongbaoRoom, table = 1): string {
  return table === 1 ? String(room) : `${room}-${table}`;
}

/** "88" / "88-2" / 88 → { room, table }; anything else → null */
export function parseHongbaoTableId(v: unknown): { room: HongbaoRoom; table: number } | null {
  const m = typeof v === 'number' ? [String(v), String(v)] : typeof v === 'string' ? /^0*(\d{1,4})(?:-(\d))?$/.exec(v) : null;
  if (!m) return null;
  const room = Number(m[1]);
  const table = m[2] === undefined ? 1 : Number(m[2]);
  if (!(HB_ROOMS as readonly number[]).includes(room) || !(HB_TABLE_NOS as readonly number[]).includes(table)) return null;
  return { room: room as HongbaoRoom, table };
}

/** open = taking seats (countdown once 2+ are in), opening = result published, envelopes fly out, finished = seed revealed */
export const HB_PHASES = ['open', 'opening', 'finished'] as const;
export type HongbaoPhase = (typeof HB_PHASES)[number];

/**
 * POST /api/hongbao/bet — take a seat at a table of the stake (`room`); the server picks the table.
 * `roundId` (optional): the open round the client sees — the seat is taken there while it has room; a round that is
 * no longer open → ROUND_ALREADY_STARTED; a full one → another table of the stake.
 */
export const zHongbaoBet = z.object({ room: zHongbaoRoom, roundId: z.string().uuid().optional(), idempotencyKey: zIdempotencyKey }).strict();
export type HongbaoBetInput = z.infer<typeof zHongbaoBet>;

/**
 * A seat. The house bot (docs/superpowers/specs/2026-10-01-house-bots-design.md): `playerId` "bot", `nickname` empty,
 * `bot: true` — it takes the second seat with the house's tokens when a lone player waited `botDelayMs`.
 */
export interface HongbaoPlayerView { playerId: string; nickname: string; avatar: string | null; position: number; bot?: true }
/** `HongbaoPlayerView.playerId` of the house bot */
export const HB_BOT_PLAYER_ID = 'bot';
export interface HongbaoTimings { countdownMs: number; openingMs: number; finishedMs: number }

/** Результат публикуется в начале `opening`; serverSeed раскрывается в `finished`. */
export interface HongbaoResult {
  bank: number; fee: number; pool: number;
  envelopes: number[];      // суммы конвертов по индексу конверта
  assignment: number[];     // assignment[position] = индекс конверта
  luckiest: number;         // position с наибольшим конвертом (при равенстве — меньший position)
}
export interface HongbaoRoundResult extends HongbaoResult { stake: number; entries: { id: string; position: number; bot?: true }[] } // GameRound.result

export interface HongbaoHistoryItem {
  roundId: string; roundNo: number; bank: number; players: number; luckiestNickname: string; luckiestAmount: number;
  /** the biggest envelope went to the house bot (`luckiestNickname` is empty) */
  luckiestBot?: boolean;
}

/** Open tables of a stake. */
export interface HongbaoTableCount { room: HongbaoRoom; tables: number }

/**
 * GET /api/hongbao/state?room=88[&table=2], ack of `hongbao:join`, and the `hongbao:state` push (every phase change).
 * Without `table` the server answers with the table a newcomer would watch: the open one with the most players.
 */
export interface HongbaoState {
  room: HongbaoRoom;
  /** table number of the stake (`HB_TABLE_NOS`); absent (older servers) = 1 */
  table?: number;
  phase: HongbaoPhase;
  roundId: string; roundNo: number; serverSeedHash: string; serverSeed: string | null; clientSeed: string;
  players: HongbaoPlayerView[]; seq: number;
  countdownEndsAt: number | null; phaseStartedAt: number; phaseEndsAt: number | null; serverNow: number;
  timings: HongbaoTimings; minPlayers: number; maxPlayers: number; feeBps: number;
  result: HongbaoResult | null;
  /** last rounds of this table */
  history: HongbaoHistoryItem[];
  /** open tables per stake (all stakes); absent on older servers */
  tableCounts?: HongbaoTableCount[];
  /** epoch ms when the house bot takes the second seat if nobody else sits down; null = no bot due */
  botAt?: number | null;
  /** how long a lone player waits for a house bot, ms; null / absent = house bots are off */
  botDelayMs?: number | null;
}
export interface HongbaoPlayersEvent { room: HongbaoRoom; table?: number; roundId: string; seq: number; players: HongbaoPlayerView[]; countdownEndsAt: number | null; serverNow: number }
/** `hongbao:tables` — the number of tables of some stake changed (sent to every table's socket room). */
export interface HongbaoTablesEvent { tableCounts: HongbaoTableCount[] }
/** `hongbao:closed` — a spare table closed (its watchers join the stake again). */
export interface HongbaoClosedEvent { room: HongbaoRoom; table: number }
/** POST /api/hongbao/bet response; the player's sockets are also joined to the table's room. */
export interface HongbaoBetResult { room: HongbaoRoom; table?: number; roundId: string; player: HongbaoPlayerView; balance: number; duplicate: boolean }

export interface HongbaoServerToClient {
  'hongbao:state': (s: HongbaoState) => void;
  'hongbao:players': (e: HongbaoPlayersEvent) => void;
  'hongbao:tables': (e: HongbaoTablesEvent) => void;
  'hongbao:closed': (e: HongbaoClosedEvent) => void;
}
/**
 * `hongbao:join(room | tableId, ack)`: a stake (18) → the open table with the most players; a table id ("88-2") → that
 * table. The socket leaves the other tables of the stake. `hongbao:leave(room | tableId)`: a stake leaves all its tables.
 */
export interface HongbaoClientToServer {
  'hongbao:join': (room: HongbaoRoom | string, ack: (r: HongbaoState | ApiError) => void) => void;
  'hongbao:leave': (room: HongbaoRoom | string) => void;
}
